import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  MACRO: "Macro & Global",
  REGIONAL: "Regional Markets",
  ASSET: "Asset Class",
  STRATEGY: "Strategy & Instruments",
  QUANT: "Quant & Systematic",
  FUNDAMENTAL: "Fundamentals",
  FLOW: "Institutional Flow",
  PORTFOLIO: "Portfolio & Risk",
};

export default function Agents() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { slug } = useParams();
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState([]);
  const [topic, setTopic] = useState("");
  const [symbol, setSymbol] = useState("");
  const [roundtableResult, setRoundtableResult] = useState(null);
  const [rtLoading, setRtLoading] = useState(false);
  const [category, setCategory] = useState("ALL");

  useEffect(() => {
    api.get("/agents").then(({ data }) => setAgents(data.agents || []));
  }, []);

  useEffect(() => {
    // If we navigate to /agents/:slug, redirect to detail page
    if (slug) navigate(`/agents/${slug}`, { replace: true });
  }, [slug, navigate]);

  const grouped = useMemo(() => {
    const filtered = category === "ALL" ? agents : agents.filter((a) => a.category === category);
    const byCat = {};
    filtered.forEach((a) => {
      byCat[a.category] = byCat[a.category] || [];
      byCat[a.category].push(a);
    });
    return byCat;
  }, [agents, category]);

  const toggleAgent = (id) => {
    setSelected((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : s.length >= 5 ? s : [...s, id]
    );
  };

  const runRoundtable = async () => {
    if (!user) {
      toast.error("Sign in to run a roundtable");
      return;
    }
    if (selected.length < 2) {
      toast.error("Select at least 2 agents");
      return;
    }
    if (!topic.trim()) {
      toast.error("Enter a topic");
      return;
    }
    setRtLoading(true);
    setRoundtableResult(null);
    try {
      const { data } = await api.post("/agents/roundtable", {
        topic: topic.trim(),
        agent_ids: selected,
        symbol: symbol.trim() || null,
      });
      setRoundtableResult(data);
      toast.success(`Roundtable synthesized · ${data.voices.length} voices`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Roundtable failed");
    } finally {
      setRtLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="agents-page">
      <div className="border-b border-[color:var(--border-default)] pb-4 mb-6">
        <div className="text-[10px] font-mono-data text-[#FFD700] tracking-widest mb-2">
          ◆ 30 SPECIALIST AI AGENTS · TRADING FLOOR
        </div>
        <h1 className="font-serif-elite text-4xl md:text-5xl font-bold tracking-tight">
          The Trading Floor
        </h1>
        <p className="text-[color:var(--text-secondary)] text-sm mt-2 max-w-2xl">
          Talk 1-on-1 with any specialist, or convene a{" "}
          <span className="text-gold">Multi-Agent Roundtable</span> where 2–5 experts debate your
          topic and the CIO synthesizes an actionable trade.
        </p>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="category-filter">
        <button
          data-testid="cat-ALL"
          onClick={() => setCategory("ALL")}
          className={`text-[10px] font-mono-data px-3 py-1.5 border border-[color:var(--border-default)] tracking-widest transition-colors ${
            category === "ALL" ? "bg-white text-black" : "text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-hover)]"
          }`}
        >
          ALL 30
        </button>
        {Object.keys(CATEGORY_LABELS).map((c) => (
          <button
            key={c}
            data-testid={`cat-${c}`}
            onClick={() => setCategory(c)}
            className={`text-[10px] font-mono-data px-3 py-1.5 border border-[color:var(--border-default)] tracking-widest transition-colors ${
              category === c ? "bg-white text-black" : "text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-hover)]"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Agent grid */}
      <div className="space-y-6 mb-8">
        {Object.entries(grouped).map(([cat, list]) => (
          <div key={cat}>
            <div className="flex items-baseline gap-3 mb-2">
              <h2 className="text-[11px] font-mono-data tracking-widest text-white">{cat}</h2>
              <span className="text-[10px] font-body text-[color:var(--text-muted)]">
                {CATEGORY_LABELS[cat]} · {list.length} agents
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-[color:var(--border-default)]">
              {list.map((a) => {
                const isSel = selected.includes(a.id);
                return (
                  <div
                    key={a.id}
                    data-testid={`agent-card-${a.id}`}
                    className={`p-4 border-r border-b border-[color:var(--border-default)] transition-colors ${
                      isSel ? "bg-[color:var(--bg-hover)]" : "hover:bg-[color:var(--bg-elevated)]"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div
                          className="w-2 h-2 shrink-0"
                          style={{ backgroundColor: a.color }}
                          aria-hidden
                        />
                        <div className="text-white text-sm font-medium truncate">{a.name}</div>
                      </div>
                      <span className="text-[9px] font-mono-data text-[color:var(--text-muted)] border border-[color:var(--border-default)] px-1.5 py-0.5 tracking-widest">
                        {a.tier?.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-[11px] font-body text-[color:var(--text-secondary)] leading-relaxed mb-3 min-h-[32px]">
                      {a.role}
                    </p>
                    <div className="flex gap-2">
                      <Link
                        to={`/agents/${a.id}`}
                        data-testid={`chat-agent-${a.id}`}
                        className="flex-1 text-[10px] font-mono-data tracking-widest text-center py-1.5 bg-white text-black hover:opacity-90 transition-colors"
                      >
                        CHAT →
                      </Link>
                      <button
                        data-testid={`select-agent-${a.id}`}
                        onClick={() => toggleAgent(a.id)}
                        className={`text-[10px] font-mono-data tracking-widest px-2 py-1.5 border transition-colors ${
                          isSel
                            ? "border-gold text-gold"
                            : "border-[color:var(--border-default)] text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-hover)]"
                        }`}
                        aria-pressed={isSel}
                      >
                        {isSel ? "✓ SELECTED" : "+ ROUNDTABLE"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Roundtable panel */}
      <div className="border border-gold bg-[color:var(--bg-elevated)] p-5" data-testid="roundtable-panel">
        <div className="flex items-baseline gap-3 mb-3">
          <span className="text-gold font-mono-data text-[10px] tracking-widest">◆ MULTI-AGENT ROUNDTABLE</span>
          <span className="text-[10px] font-body text-[color:var(--text-muted)]">
            2–5 agents · Costs 30 credits · Synthesizes into one institutional call
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr,180px,140px] gap-2 mb-3">
          <input
            data-testid="rt-topic-input"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. Bull thesis for NVDA next 6 months"
            className="bg-[color:var(--bg-surface)] border border-[color:var(--border-default)] px-3 py-2.5 text-sm text-white placeholder:text-[color:var(--text-muted)] focus:outline-none focus:border-[color:var(--border-focus)]"
          />
          <input
            data-testid="rt-symbol-input"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            placeholder="Ticker (optional)"
            className="bg-[color:var(--bg-surface)] border border-[color:var(--border-default)] px-3 py-2.5 text-sm text-white placeholder:text-[color:var(--text-muted)] focus:outline-none focus:border-[color:var(--border-focus)] font-mono-data"
          />
          <button
            data-testid="run-roundtable-btn"
            onClick={runRoundtable}
            disabled={rtLoading || selected.length < 2 || !topic.trim()}
            className="bg-gold text-black px-4 py-2.5 text-xs font-mono-data tracking-widest hover:opacity-90 disabled:opacity-40 transition-opacity"
            style={{ backgroundColor: "#FFD700", color: "#000" }}
          >
            {rtLoading ? "CONVENING…" : `CONVENE (${selected.length})`}
          </button>
        </div>
        <div className="text-[10px] font-mono-data text-[color:var(--text-muted)]">
          SELECTED: {selected.length ? selected.join(" · ") : "none — pick 2–5 above"}
        </div>
      </div>

      {rtLoading && (
        <div className="border border-[color:var(--border-default)] p-8 text-center text-xs font-mono-data text-[color:var(--text-muted)] mt-4" data-testid="rt-loading">
          Specialist agents in parallel synthesis · CIO moderator standing by…
        </div>
      )}

      {roundtableResult && <RoundtableReport report={roundtableResult} />}
    </div>
  );
}

function RoundtableReport({ report }) {
  return (
    <div className="mt-6 space-y-4" data-testid="roundtable-report">
      <div className="border border-gold bg-[color:var(--bg-surface)] p-5">
        <div className="text-[10px] font-mono-data text-gold tracking-widest mb-2">
          ◆ CIO SYNTHESIS · CONSENSUS &amp; DIVERGENCE
        </div>
        <h3 className="font-serif-elite text-2xl mb-3 text-white">{report.topic}</h3>
        <pre className="whitespace-pre-wrap font-body text-sm text-white leading-relaxed">
          {report.synthesis}
        </pre>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[color:var(--border-default)]">
        {report.voices.map((v) => (
          <div key={v.id} data-testid={`voice-${v.id}`} className="p-4 border-r border-b border-[color:var(--border-default)]">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2" style={{ backgroundColor: v.color }} aria-hidden />
              <span className="text-white text-sm font-medium">{v.name}</span>
              <span className="text-[9px] font-mono-data text-[color:var(--text-muted)] ml-auto">{v.role}</span>
            </div>
            <div className="whitespace-pre-wrap font-body text-[12px] text-[color:var(--text-secondary)] leading-relaxed">
              {v.content}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Re-export helper unused
export { API };
